import { z } from 'zod';
import { baseMessageSchema } from './_base';
import { ClientType } from './shared';

export enum ConnectionMessageType {
  CONN_INIT = 'CONN_INIT',
  CONN_AUTH_REQUIRED = 'CONN_AUTH_REQUIRED',
  CONN_INIT_SUCCESS = 'CONN_INIT_SUCCESS',
  CONN_INIT_ERROR = 'CONN_INIT_ERROR',
  CONN_CLOSE = 'CONN_CLOSE',
  CONN_CLOSED = 'CONN_CLOSED',
}

export type WebSocketId = string;
export type AuthId = string;
export type PersistentId = string;
export type MachineId = string;
export type ServerId = string;
export type InvitationId = string;
export type PlayerId = string;

export type PlayerConnection = {
  type: ClientType.PLAYER;
  websocketId: WebSocketId;
  authId?: AuthId;
  persistentId?: PersistentId;
  timestamp: number;
};

export type HostConnection = {
  type: ClientType.HOST;
  websocketId: WebSocketId;
  machineId: MachineId;
  authId?: AuthId;
  timestamp: number;
};

export type RemoteHostConnection = {
  type: ClientType.REMOTE_HOST;
  websocketId: WebSocketId;
  machineId: MachineId;
  authId?: AuthId;
  timestamp: number;
};

export type RemoteClientConnection = {
  type: ClientType.REMOTE_CLIENT;
  websocketId: WebSocketId;
  persistentId?: PersistentId;
  authId?: AuthId;
  timestamp: number;
};

export type ObserverConnection = {
  type: ClientType.OBSERVER;
  websocketId: WebSocketId;
  persistentId?: PersistentId;
  authId?: AuthId;
  timestamp: number;
};

export type ServerInfo = {
  serverId: ServerId;
  lastPing: number;
  activeConnections: Set<WebSocketId>;
};

// Payload schemas
const connectionInitPayloadSchema = z.object({
  version: z.string(),
  // Add any other init data needed
});

const connectionAuthRequiredPayloadSchema = z.object({
  reason: z.string(),
});

const connectionInitSuccessPayloadSchema = z.object({});

const connectionInitErrorPayloadSchema = z.object({
  error: z.object({
    code: z.string(),
    message: z.string(),
  }),
});

const connectionClosePayloadSchema = z.object({
  reason: z.string().optional(),
});

const connectionClosedPayloadSchema = z.object({
  reason: z.string().optional(),
});

// Full message schemas
const connectionInitSchema = baseMessageSchema
  .extend({
    type: z.literal(ConnectionMessageType.CONN_INIT),
  })
  .merge(connectionInitPayloadSchema);

const connectionAuthRequiredSchema = baseMessageSchema
  .extend({
    type: z.literal(ConnectionMessageType.CONN_AUTH_REQUIRED),
  })
  .merge(connectionAuthRequiredPayloadSchema);

const connectionInitSuccessSchema = baseMessageSchema
  .extend({
    type: z.literal(ConnectionMessageType.CONN_INIT_SUCCESS),
  })
  .merge(connectionInitSuccessPayloadSchema);

const connectionInitErrorSchema = baseMessageSchema
  .extend({
    type: z.literal(ConnectionMessageType.CONN_INIT_ERROR),
  })
  .merge(connectionInitErrorPayloadSchema);

const connectionCloseSchema = baseMessageSchema
  .extend({
    type: z.literal(ConnectionMessageType.CONN_CLOSE),
  })
  .merge(connectionClosePayloadSchema);

const connectionClosedSchema = baseMessageSchema
  .extend({
    type: z.literal(ConnectionMessageType.CONN_CLOSED),
  })
  .merge(connectionClosedPayloadSchema);

export const connectionMessageSchema = z.discriminatedUnion('type', [
  connectionInitSchema,
  connectionAuthRequiredSchema,
  connectionInitSuccessSchema,
  connectionInitErrorSchema,
  connectionCloseSchema,
  connectionClosedSchema,
]);

// Payload types
export type ConnectionInitPayload = z.infer<typeof connectionInitPayloadSchema>;
export type connectionAuthRequiredPayload = z.infer<typeof connectionAuthRequiredPayloadSchema>;
export type ConnectionInitSuccessPayload = z.infer<typeof connectionInitSuccessPayloadSchema>;
export type ConnectionInitErrorPayload = z.infer<typeof connectionInitErrorPayloadSchema>;
export type ConnectionClosePayload = z.infer<typeof connectionClosePayloadSchema>;
export type ConnectionClosedPayload = z.infer<typeof connectionClosedPayloadSchema>;

// Full message type
export type ConnectionMessage = z.infer<typeof connectionMessageSchema>;

type PayloadType<T extends ConnectionMessageType> = T extends ConnectionMessageType.CONN_INIT
  ? ConnectionInitPayload
  : T extends ConnectionMessageType.CONN_AUTH_REQUIRED
    ? connectionAuthRequiredPayload
    : T extends ConnectionMessageType.CONN_INIT_SUCCESS
      ? ConnectionInitSuccessPayload
      : T extends ConnectionMessageType.CONN_INIT_ERROR
        ? ConnectionInitErrorPayload
        : T extends ConnectionMessageType.CONN_CLOSE
          ? ConnectionClosePayload
          : T extends ConnectionMessageType.CONN_CLOSED
            ? ConnectionClosedPayload
            : never;

export function generateConnectionMessage<T extends ConnectionMessageType>(type: T, payload: PayloadType<T>): ConnectionMessage {
  const base = {
    id: crypto.randomUUID(),
    timestamp: Date.now(),
    type,
  };

  switch (type) {
    case ConnectionMessageType.CONN_INIT:
      return connectionInitSchema.parse({
        ...base,
        ...payload,
      });
    case ConnectionMessageType.CONN_AUTH_REQUIRED:
      return connectionAuthRequiredSchema.parse({
        ...base,
        ...payload,
      });
    case ConnectionMessageType.CONN_INIT_SUCCESS:
      return connectionInitSuccessSchema.parse({
        ...base,
        ...payload,
      });
    case ConnectionMessageType.CONN_INIT_ERROR:
      return connectionInitErrorSchema.parse({
        ...base,
        ...payload,
      });
    case ConnectionMessageType.CONN_CLOSE:
      return connectionCloseSchema.parse({
        ...base,
        ...payload,
      });
    case ConnectionMessageType.CONN_CLOSED:
      return connectionClosedSchema.parse({
        ...base,
        ...payload,
      });
    default: {
      const exhaustiveCheck: never = type;
      throw new Error(`Unhandled message type: ${exhaustiveCheck}`);
    }
  }
}
